"use client";

import type { FormVariant } from "@homeinstead/analytics";
import type { ButtonVariantProps } from "@homeinstead/ui/components/Button";
import type { ReactElement } from "react";
import type { ControllerRenderProps, FieldPath } from "react-hook-form";
import type {
  CareRecipient,
  CareSeekerFormDataVariant,
} from "~www/types/forms/care-seeker-shallow-lead-form";

import {
  ServiceRecipientTrackedClickEvent,
  track,
} from "@homeinstead/analytics";
import { EventName, scrollToTop } from "@homeinstead/shared";
import { Button } from "@homeinstead/ui/components/Button";
import { useEffect, useState } from "react";
import { trackLegacyLeadFormStep } from "./track-legacy-lead-form-step";

export const CareRecipientButton = <T extends CareSeekerFormDataVariant>({
  field,
  careRecipient,
  nextStepCallback,
  className,
  children,
  buttonVariant = "primary",
  formVariant,
}: {
  field: ControllerRenderProps<T, FieldPath<T>>;
  careRecipient: CareRecipient;
  nextStepCallback: () => void;
  className: string;
  children: ReactElement | ReactElement[];
  buttonVariant?: ButtonVariantProps["variant"];
  formVariant: FormVariant;
}) => {
  // This is a workaround to prevent the button from reading the window object on the server.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    // TODO stop suppressing this warning (don't replicate this!)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsClient(true);
  }, []);

  const handleNextStep = ({
    field,
    careRecipient,
  }: {
    field: ControllerRenderProps<T, FieldPath<T>>;
    careRecipient: CareRecipient;
  }) => {
    if (!isClient) {
      console.warn("Calling handleNextStep on the server");
      return;
    }

    field.onChange(careRecipient);
    track(
      EventName.TRACKED_CLICK,
      ServiceRecipientTrackedClickEvent(careRecipient, formVariant),
    );
    trackLegacyLeadFormStep({
      formVariant,
      stepName: "recipient",
      userSelection: { careFor: careRecipient },
    });

    // Scroll to the top of the page on each new step
    scrollToTop();

    nextStepCallback();
  };

  return (
    <Button
      className={className}
      onClick={() => handleNextStep({ careRecipient, field })}
      type="button"
      variant={buttonVariant}
    >
      {children}
    </Button>
  );
};
